import { useEffect, useState } from "react";
import type { GoalsMap, MonthlyRow } from "@/components/teams/metrics-tracker-table";
import { MetricsTrackerTable } from "@/components/teams/metrics-tracker-table";

/**
 * Annual goals for the Faithful Platform — compared against the
 * "Total / Avg" column. `comparison` indicates which direction is "good":
 * - "gte": meeting or exceeding the goal is good (e.g. close rate, sales).
 * - "lte": staying at or under the goal is good (e.g. error rate).
 */
const GOALS: GoalsMap = {
  opportunities: { value: 9882, comparison: "gte" },
  closedDeals: { value: 5680, comparison: "gte" },
  closeRate: { value: 0.6, comparison: "gte" },
  grossSales: { value: 10_000_000, comparison: "gte" },
  netSales: { value: 4_000_000, comparison: "gte" },
  avgOrderValue: { value: 1760.56, comparison: "gte" },
  grossProfitMargin: { value: 0.4, comparison: "gte" },
  errorRate: { value: 0.01, comparison: "lte" },
};

export function FpConsultantsMetricsChart() {
  const [rows, setRows] = useState<MonthlyRow[] | null>(null);
  const [fiscalYear, setFiscalYear] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        setLoading(true);
        setError(null);
        const res = await fetch("/api/fp-consultants/monthly-metrics");
        if (!res.ok) {
          throw new Error(`Failed to load metrics (${res.status})`);
        }
        const data = await res.json();
        if (!cancelled) {
          setRows(data.monthly ?? []);
          setFiscalYear(data.fiscalYear ?? null);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : "Failed to load metrics");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, []);

  if (loading) {
    return <div className="text-sm text-muted-foreground">Loading Faithful Platform metrics…</div>;
  }

  if (error) {
    return <div className="text-sm text-destructive">{error}</div>;
  }

  if (!rows || rows.length === 0) {
    return <div className="text-sm text-muted-foreground">No monthly metrics found yet.</div>;
  }

  return (
    <MetricsTrackerTable
      rows={rows}
      fiscalYear={fiscalYear}
      goals={GOALS}
      title="Faithful Platform Monthly Metrics"
      subtitle="Faithful Platform"
    />
  );
}
